import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import components (lazy-loaded for performance)
const LMCourseList = React.lazy(() => import("./LMCourseList"));
const LMCourseView = React.lazy(() => import("./LMCourseView"));
const LMCourseEditor = React.lazy(() => import("./LMCourseEditor"));
const LMModuleEditor = React.lazy(() => import("./LMModuleEditor"));
const LMLessonEditor = React.lazy(() => import("./LMLessonEditor"));
const LMLessonView = React.lazy(() => import("./LMLessonView"));
const LMCourseAdmin = React.lazy(() => import("./LMCourseAdmin"));
const LMPublicCourseView = React.lazy(() => import("./LMPublicCourseView"));
const LMSharedLessonView = React.lazy(() => import("./LMSharedLessonView"));
const LMDashboard = React.lazy(() => import("./LMDashboard"));
const LMCertificateView = React.lazy(() => import("./LMCertificateView"));
const LMMyCertificates = React.lazy(() => import("./LMMyCertificates"));
const LMAnalytics = React.lazy(() => import("./LMAnalytics"));
const LMWhatsNew = React.lazy(() => import("./LMWhatsNew"));

const lmModuleConfig: ModuleConfig = {
    // 🏷️ MODULE IDENTITY
    id: "lm",
    name: "Learning Management",
    description: "Create and manage online courses with video lessons, progress tracking, and certificates",
    version: "0.0.1",
    enabled: true,
    category: "education",
    order: 17,
    icon: "🎓",
    color: "#3B82F6",

    // 📝 VERSION CHANGELOG
    // Track what's new in each release
    changelog: [
        {
            version: "1.0.0",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production LMS",
            features: [
                "✅ Production-ready learning management system",
                "✅ Complete course creation and delivery platform",
                "✅ Full module configuration integration",
                "✅ Comprehensive permissions system",
                "✅ Student and instructor dashboards",
                "✅ Certificate generation and verification"
            ],
            notes: "Official stable release with complete LMS functionality for production use."
        },
        {
            version: "0.2.0",
            releaseDate: "2025-11-17",
            title: "Modular What's New System",
            features: [
                "✨ Created modular What's New architecture",
                "✨ Integrated changelog display in module",
                "✨ Roadmap visualization",
                "✨ Centralized version tracking",
                "✨ Enhanced module documentation"
            ],
            notes: "Enhanced module system with centralized changelog and roadmap management."
        },
        {
            version: "0.1.0",
            releaseDate: "2024-11-14",
            title: "Module Configuration & Tokenization",
            features: [
                "✅ Created comprehensive moduleConfig.ts (454 lines)",
                "✅ Defined all routes and permissions",
                "✅ Added navigation structure",
                "✅ Configured quick actions",
                "✅ Tokenization system integration",
                "✅ Business Builder system integration"
            ],
            notes: "Formal module configuration and system tokenization implementation."
        },
        {
            version: "0.0.1",
            releaseDate: "2023-11-14",
            title: "Initial Release - Core LMS Features",
            features: [
                "✅ Course creation and management with markdown import",
                "✅ Module and lesson organization with drag-and-drop reordering",
                "✅ Video upload with chunked IPFS storage",
                "✅ Markdown content editor with live preview",
                "✅ Attachment system (PDFs, worksheets, documents)",
                "✅ Student enrollment and progress tracking",
                "✅ Mark lessons complete/incomplete",
                "✅ Progress bar and completion indicators",
                "✅ Resume learning (last accessed lesson)",
                "✅ Complete 7-Day Business Builder Bootcamp course (24 lessons)",
                "✅ Public and private course support",
                "✅ Free preview lessons",
                "🔨 Student dashboard (placeholder)",
                "🔨 Certificate system (placeholder)",
                "🔨 Analytics dashboard (placeholder)"
            ],
            notes: "Initial massive implementation with complete LMS platform (7,000+ lines of code). All core features and routing configured."
        }
    ],

    // 🗺️ ROADMAP - Coming Soon Features
    // Track planned features and their development status
    roadmap: [
        {
            title: "Student Dashboard",
            description: "Personalized dashboard showing enrolled courses, progress, and recommended next steps",
            targetVersion: "0.1.0",
            priority: "high",
            status: "planned",
            features: [
                "View all enrolled courses with progress bars",
                "Continue where you left off (last accessed lesson)",
                "Recently completed lessons history",
                "Course recommendations based on interests",
                "Time spent learning statistics"
            ]
        },
        {
            title: "Certificate System",
            description: "Automated certificate generation and public sharing for completed courses",
            targetVersion: "0.1.0",
            priority: "high",
            status: "planned",
            features: [
                "Auto-generate certificates on course completion",
                "Beautiful certificate design with branding",
                "Public certificate verification pages",
                "Download certificates as PDF",
                "Share certificates on LinkedIn",
                "Certificate verification via unique ID"
            ]
        },
        {
            title: "Analytics & Reporting",
            description: "Comprehensive analytics for instructors and admins to track course performance",
            targetVersion: "0.2.0",
            priority: "medium",
            status: "planned",
            features: [
                "Real-time enrollment tracking",
                "Course completion rates",
                "Student engagement metrics",
                "Popular courses dashboard",
                "Revenue tracking (if applicable)",
                "Export reports as CSV/PDF"
            ]
        },
        {
            title: "Interactive Features",
            description: "Quizzes, assignments, and interactive learning elements",
            targetVersion: "0.3.0",
            priority: "medium",
            status: "planned",
            features: [
                "Multiple choice quizzes",
                "Assignment submissions",
                "Grading and feedback system",
                "Discussion forums per lesson",
                "Q&A for students and instructors"
            ]
        },
        {
            title: "Course Builder Enhancements",
            description: "Advanced tools for course creation and management",
            targetVersion: "0.2.0",
            priority: "low",
            status: "planned",
            features: [
                "Bulk lesson import from multiple markdown files",
                "Course cloning/duplication",
                "Course templates library",
                "Automatic video transcription",
                "Subtitle/caption support",
                "Multi-language course support"
            ]
        },
        {
            title: "SMS & Email Campaign Integration",
            description: "Automated notifications and drip campaigns tied to course progress",
            targetVersion: "0.3.0",
            priority: "low",
            status: "planned",
            features: [
                "Daily lesson reminders via SMS",
                "Email drip campaigns based on enrollment",
                "Progress check-in notifications",
                "Completion congratulations",
                "Re-engagement campaigns for inactive students"
            ]
        },
        {
            title: "Social & Community Features",
            description: "Build community and engagement around courses",
            targetVersion: "0.4.0",
            priority: "low",
            status: "planned",
            features: [
                "Course ratings and reviews",
                "Student testimonials",
                "Discussion forums",
                "Student profiles and achievements",
                "Instructor profiles and bios",
                "Course bundles and learning paths"
            ]
        }
    ],

    // 🛣️ ROUTES
    routes: [
        // Private Routes (Authentication required for management)
        {
            path: "/lm",
            component: LMCourseList,
            permissions: ["USER"] // All logged-in users can browse courses
        },
        {
            path: "/lm/courses/:id",
            component: LMCourseView,
            permissions: ["USER"] // All logged-in users can view course details
        },
        {
            path: "/lm/courses/new",
            component: LMCourseEditor,
            permissions: [
                "ADMIN",
                "LM_ADMIN",
                "LM_INSTRUCTOR"
            ]
        },
        {
            path: "/lm/courses/:id/edit",
            component: LMCourseEditor,
            permissions: [
                "ADMIN",
                "LM_ADMIN",
                "LM_INSTRUCTOR"
            ]
        },
        {
            path: "/lm/courses/:courseId/modules/new",
            component: LMModuleEditor,
            permissions: [
                "ADMIN",
                "LM_ADMIN",
                "LM_INSTRUCTOR"
            ]
        },
        {
            path: "/lm/courses/:courseId/modules/:id/edit",
            component: LMModuleEditor,
            permissions: [
                "ADMIN",
                "LM_ADMIN",
                "LM_INSTRUCTOR"
            ]
        },
        {
            path: "/lm/courses/:courseId/modules/:moduleId/lessons/new",
            component: LMLessonEditor,
            permissions: [
                "ADMIN",
                "LM_ADMIN",
                "LM_INSTRUCTOR"
            ]
        },
        {
            path: "/lm/courses/:courseId/modules/:moduleId/lessons/:id/edit",
            component: LMLessonEditor,
            permissions: [
                "ADMIN",
                "LM_ADMIN",
                "LM_INSTRUCTOR"
            ]
        },
        {
            path: "/lm/courses/:courseId/modules/:moduleId/lessons/:id",
            component: LMLessonView,
            permissions: [] // No route-level auth check - component handles login modal for enrollment
        },
        {
            path: "/lm/lesson/:id/edit",
            component: LMLessonEditor,
            permissions: [
                "ADMIN",
                "LM_ADMIN",
                "LM_INSTRUCTOR"
            ]
        },
        {
            path: "/lm/lesson/:id",
            component: LMLessonView,
            permissions: [] // No route-level auth check - component handles login modal for enrollment
        },
        {
            path: "/lm/admin",
            component: LMCourseAdmin,
            permissions: [
                "ADMIN",
                "LM_ADMIN"
            ]
        },
        // Student Dashboard & Certificates
        {
            path: "/lm/dashboard",
            component: LMDashboard,
            permissions: ["USER"] // Student dashboard - enrolled courses
        },
        {
            path: "/lm/my-certificates",
            component: LMMyCertificates,
            permissions: ["USER"] // List of user's certificates
        },
        {
            path: "/lm/analytics",
            component: LMAnalytics,
            permissions: [
                "ADMIN",
                "LM_ADMIN",
                "LM_INSTRUCTOR"
            ] // Analytics for instructors/admins
        },
        {
            path: "/lm/whats-new",
            component: LMWhatsNew,
            permissions: [
                "ADMIN",
                "LM_ADMIN",
                "LM_INSTRUCTOR"
            ] // What's New (changelog/roadmap) for admins/instructors
        },
        // Public Certificate View (No auth required)
        {
            path: "/lm/certificate/:slug",
            component: LMCertificateView,
            permissions: [] // Public certificate page
        },
        // Shared Link Route (Requires login - for team/internal sharing)
        {
            path: "/lm/shared/:token",
            component: LMSharedLessonView,
            permissions: ["USER"] // Login required for shared courses
        },
        // Completely Public Route (No login required - for marketing/public courses)
        {
            path: "/lm/course/:slug",
            component: LMPublicCourseView,
            permissions: [] // Completely public - videos and content load without login
        }
    ],

    // 🧭 NAVIGATION
    navigation: [
        {
            label: "All Courses",
            path: "/lm",
            icon: "📚",
            permissions: ["USER"] // Show to all users
        },
        {
            label: "My Dashboard",
            path: "/lm/dashboard",
            icon: "🎯",
            permissions: ["USER"] // Student dashboard
        },
        {
            label: "My Certificates",
            path: "/lm/my-certificates",
            icon: "🏆",
            permissions: ["USER"] // User's certificates
        },
        {
            label: "Create Course",
            path: "/lm/courses/new",
            icon: "➕",
            permissions: [
                "ADMIN",
                "LM_ADMIN",
                "LM_INSTRUCTOR"
            ]
        },
        {
            label: "Analytics",
            path: "/lm/analytics",
            icon: "📊",
            permissions: [
                "ADMIN",
                "LM_ADMIN",
                "LM_INSTRUCTOR"
            ]
        },
        {
            label: "Admin Dashboard",
            path: "/lm/admin",
            icon: "⚙️",
            permissions: [
                "ADMIN",
                "LM_ADMIN"
            ]
        },
        {
            label: "What's New",
            path: "/lm/whats-new",
            icon: "✨",
            permissions: [
                "ADMIN",
                "LM_ADMIN",
                "LM_INSTRUCTOR"
            ]
        }
    ],

    // 🔐 PERMISSIONS
    permissions: {
        view: ["USER"], // Any user can view public courses
        create: [
            "ADMIN",
            "LM_ADMIN",
            "LM_INSTRUCTOR"
        ],
        edit: [
            "ADMIN",
            "LM_ADMIN",
            "LM_INSTRUCTOR"
        ],
        delete: [
            "ADMIN",
            "LM_ADMIN"
        ],
        publish: [
            "ADMIN",
            "LM_ADMIN",
            "LM_INSTRUCTOR"
        ]
    },

    // ⚡ QUICK ACTIONS
    quickActions: [
        {
            label: "My Dashboard",
            path: "/lm/dashboard",
            icon: "🎯",
            description: "View your enrolled courses and progress"
        },
        {
            label: "Browse Courses",
            path: "/lm",
            icon: "📚",
            description: "View all available courses"
        },
        {
            label: "My Certificates",
            path: "/lm/my-certificates",
            icon: "🏆",
            description: "View your earned certificates"
        },
        {
            label: "Create Course",
            path: "/lm/courses/new",
            icon: "➕",
            description: "Create a new course with modules and lessons"
        },
        {
            label: "Analytics",
            path: "/lm/analytics",
            icon: "📊",
            description: "View course performance metrics"
        },
        {
            label: "Admin Dashboard",
            path: "/lm/admin",
            icon: "⚙️",
            description: "Manage all courses and enrollments"
        },
        {
            label: "What's New",
            path: "/lm/whats-new",
            icon: "✨",
            description: "View changelog and roadmap"
        }
    ]
};

export default lmModuleConfig;
